import type { AxiosInstance } from "axios";
import { parseDetailAddress, type DetailRouteKey } from "./detailRoutes";
import { parseTenantParam } from "./useDetailTenantId";
import { splitUrl } from "./viewsModel";

/**
 * A view's name is the resource's own name — "10-A3F7C1", not "Port Details" — so two
 * ports open side by side can be told apart (intent §5). A view opened in the background
 * has not mounted its page, so the name is asked for here, with one light request per
 * view; the page title stands in until it answers, and for any screen that is not a
 * detail.
 *
 * Only identifiers the product already shows are used (service / subscription ids,
 * names), never a database id (see the no-internal-ids rule).
 */

type Pick = (body: unknown) => string | null;

interface LabelSource {
  /** Relative to the `/v1` base of `useAxiosAuth0`, or absolute for other API versions. */
  path: (id: string, tenantId: number) => string;
  pick: Pick;
}

const API_ROOT = process.env.REACT_APP_API_URL ?? "";

function field(value: unknown, ...keys: string[]): string | null {
  let current: unknown = value;
  for (const key of keys) {
    if (!current || typeof current !== "object") return null;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "string" && current.trim() ? current.trim() : null;
}

/** `{ data: {...} }` envelopes and bare bodies both occur across the API. */
const fromData = (...candidates: string[][]): Pick => (body) => {
  for (const path of candidates) {
    const value = field(body, "data", ...path) ?? field(body, ...path);
    if (value) return value;
  }
  return null;
};

const SERVICE_ID = fromData(["subscriptionId"], ["name"]);

const SOURCES: Partial<Record<DetailRouteKey, LabelSource>> = {
  port: {
    path: (id, t) => `/kontroledge/ports/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: SERVICE_ID,
  },
  internet: {
    path: (id, t) => `/kontroledge/internet/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: SERVICE_ID,
  },
  cloudConnectivity: {
    path: (id, t) => `/kontroledge/cloud-connectivity/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: SERVICE_ID,
  },
  datacenter: {
    path: (id, t) => `/kontroledge/datacenter-interconnect/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: SERVICE_ID,
  },
  sdwanHub: {
    path: (id, t) => `/kontroledge/sdwan/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: fromData(["details", "service_id"], ["service_id"]),
  },
  bgpReflector: {
    path: (id, t) => `${API_ROOT}/v2/reflector/instances/${encodeURIComponent(id)}/overview?tenantId=${t}`,
    pick: fromData(["name"]),
  },
  cluster: {
    path: (id, t) => `${API_ROOT}/v2/reflector/clusters/${encodeURIComponent(id)}?tenantId=${t}`,
    pick: fromData(["name"]),
  },
};

/** The request that names this view, or null when its page title is all there is. */
export function labelRequestFor(url: string, fallbackTenantId: number): string | null {
  const { pathname, search } = splitUrl(url);
  const address = parseDetailAddress(pathname, search);
  if (!address) return null;
  const source = SOURCES[address.route.key];
  if (!source) return null;
  const id = Object.values(address.identity)[0];
  const tenantId = parseTenantParam(address.context.tenantId ?? null) ?? fallbackTenantId;
  if (!tenantId) return null;
  return source.path(id, tenantId);
}

export async function resolveViewLabel(
  api: AxiosInstance,
  url: string,
  fallbackTenantId: number
): Promise<string | null> {
  const request = labelRequestFor(url, fallbackTenantId);
  if (!request) return null;
  const { pathname, search } = splitUrl(url);
  const key = parseDetailAddress(pathname, search)?.route.key;
  const source = key ? SOURCES[key] : undefined;
  if (!source) return null;
  const response = await api.get(request);
  return source.pick(response.data);
}
